// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import type { ReactElement, ReactNode } from 'react';
import React, { FC, useEffect, useMemo } from 'react';

import getConfig from 'next/config';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from 'antd';
import tr_TR from 'antd/lib/locale/tr_TR';
import { FlagsProvider } from 'flagged';
import type { NextPage } from 'next';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import NextNProgress from 'nextjs-progressbar';
import { ThemeProvider } from 'styled-components';
import { Montserrat } from 'next/font/google';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { callService } from '@api/caller';
import { SFHeaderFooterCategoryFooterViewModel } from '@api/index';
import MetaInfo from '@components/views/components/MetaInfo';
import AuthProvider, { LoginInfo } from '@contexts/auth/AuthContext';
import { IsVisibleFooterProvider } from '@contexts/footer';
import { SearchBarProvider } from '@contexts/hotel/SearchBar';
import ErrorBoundary from '@layout/ErrorBoundary';
import theme from '@theme';
import AuthTokenExpiration from '@utils/auth/AuthTokenExpiration';
import { GTMPageView } from '@utils/gtm/pageView';
import { GTMScrollDepth } from '@utils/gtm/scroll-gtm';
import { withSession } from '@utils/session';
import GlobalStyles from '@utils/theme/global/GlobalStyles';
import { WindowSizeProvider } from '@contexts/windowSize';
import BreakpointsProvider from '@contexts/breakpoints/BreakpointsContext';

import FEATURE_FLAGS from '../feature-flags.json';

type NextPageWithLayout = NextPage & {
  getLayout?: (pageProps: AppProps, page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  loginInfo?: LoginInfo;
  layoutData: SFHeaderFooterCategoryFooterViewModel | null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
    },
  },
});
const URL = process.env.NEXT_PUBLIC_HOST;

const montserrat = Montserrat({
  subsets: ['latin'],
});

const { publicRuntimeConfig } = getConfig();

const App: FC<AppPropsWithLayout> = ({
  Component,
  pageProps,
  loginInfo,
  layoutData,
}): React.ReactElement => {
  const router = useRouter();

  // added for prefetch set false problem doesn't work on production
  // https://github.com/vercel/next.js/discussions/24437#discussioncomment-3679709
  useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    router.prefetch = async () => {};
  }, [router]);

  useEffect(() => {
    console.log({ version: publicRuntimeConfig?.version });
  }, []);

  const canonicalUrl = (URL + '/' + router.asPath.substring(1)).split('?')[0];

  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <>
      <Head>
        <link rel='shortcut icon' href='/favicon.ico' />
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        />
      </Head>
      <FlagsProvider features={FEATURE_FLAGS}>
        <MetaInfo canonical={canonicalUrl} />
        <ConfigProvider
          locale={tr_TR}
          prefixCls='setur'
          theme={{
            token: {
              colorPrimary: theme.palette.primaryBrand,
              fontFamily: theme.fonts.main,
              borderRadius: 2,
            },
          }}
        >
          <ThemeProvider theme={theme}>
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.NEXT_PUBLIC_SITE_KEY ?? ''}
            >
              <WindowSizeProvider>
                <style jsx global>{`
                  :root {
                    --fonts-body: ${montserrat.style.fontFamily}, sans-serif;
                  }
                `}</style>
                <GlobalStyles />
                <GTMPageView />
                <GTMScrollDepth />
                <NextNProgress
                  color={theme.palette.primaryBrand}
                  options={{
                    showSpinner: false,
                  }}
                />
                <QueryClientProvider client={queryClient}>
                  <Hydrate state={pageProps.dehydratedState}>
                    <AuthProvider loginInfo={loginInfo}>
                      <BreakpointsProvider>
                        <AuthTokenExpiration />
                        <SearchBarProvider>
                          <IsVisibleFooterProvider>
                            {getLayout(
                              <ErrorBoundary>
                                <Component {...pageProps} />
                              </ErrorBoundary>,
                              layoutData
                            )}
                          </IsVisibleFooterProvider>
                        </SearchBarProvider>
                      </BreakpointsProvider>
                    </AuthProvider>
                  </Hydrate>
                  <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
              </WindowSizeProvider>
            </GoogleReCaptchaProvider>
          </ThemeProvider>
        </ConfigProvider>
      </FlagsProvider>

      {process.env.NEXT_PUBLIC_BUILD_TYPE === 'PRODUCTION' ? (
        <Script
          id='dataroid-script'
          dangerouslySetInnerHTML={{
            __html: `(function(m, c, g, connect) {var s = m.createElement(c);s.type = g;s.async = true;s.src = connect;m.getElementsByTagName('head')[0].appendChild(s);s.onload = function(){window.dataroid.init('${process.env.NEXT_PUBLIC_DATAROID_ID}','https://garantibbva.api.dataroid.com','shopandfly-ui-staging.setur.software','seyahat.shopandfly.com.tr', {Page: {ignoreQueryParams: false,ignoreReferralQueryParams: false}});};}(document,'script','text/javascript','https://cdn.dataroid.com/sdk/web/dataroid-websdk.js'));`,
          }}
        />
      ) : null}
      <Script src='https://www.google.com/recaptcha/api.js' />
    </>
  );
};
const NApp = App as unknown as typeof NextApp;

NApp.getInitialProps = withSession(async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext);
  const loginInfo = (
    appContext.ctx as unknown as {
      loginInfo?: LoginInfo;
    }
  ).loginInfo;
  try {
    const layoutData = await callService(
      'SfContentsService',
      'sFHeaderFooterCFooter'
    );

    return {
      ...appProps,
      loginInfo,
      layoutData,
    };
  } catch (error) {
    return {
      ...appProps,
      loginInfo,
      layoutData: null,
    };
  }
});

export default NApp;
